// Data loading utilities for Pokemon Calculator
import pokemonData from '../data/pokemons.json';
import movesData from '../data/moves.json';
import abilitiesData from '../data/abilities.json';
import naturesData from '../data/natures.json';
import itemsData from '../data/items.json';

// Import all sets data
import randomSets from '../sets/random_sets.json';
import dungeonSets from '../sets/dungeon_sets.json';
import baseSets from '../sets/base_sets.json';
import gen9NatdexSd from '../sets/gen9_natdex_sd.json';

// Map of set files with their category names
const setFilesData: { [key: string]: any } = {
  'random_sets': randomSets,
  'dungeon_sets': dungeonSets,
  'base_sets': baseSets,
  'gen9_natdex_sd': gen9NatdexSd
};

// Types
export interface Pokemon {
  id: string;
  name: string;
  base_hp: number;
  base_attack: number;
  base_defense: number;
  base_sp_attack: number;
  base_sp_defense: number;
  base_speed: number;
  types: string[];
  height?: number;
  weight?: number;
}

export interface Move {
  name: string;
  type: string;
  category: 'Physical' | 'Special' | 'Status';
  power: number;
  accuracy: number;
  pp: number;
}

export interface Nature {
  name: string;
  increase: string | null;
  decrease: string | null;
}

export interface Ability {
  ability: string;
}

export interface Item {
  item: string;
}

export interface PokemonSet {
  name: string;
  category: string;
  pokedexid: string;
  nature: string;
  ability: string;
  level: number;
  item: string;
  moves: string[];
  hp_iv: number;
  atk_iv: number;
  def_iv: number;
  sp_atk_iv: number;
  sp_def_iv: number;
  spd_iv: number;
  hp_ev: number;
  atk_ev: number;
  def_ev: number;
  sp_atk_ev: number;
  sp_def_ev: number;
  spd_ev: number;
}

// Helper function to get forme name from pokedexid
export const getFormeFromPokedexId = (pokedexId: string): string => {
  const pokemon = (pokemonData as any)[pokedexId];
  return pokemon?.name || "Unknown";
};

// Data processing functions
export const getAllPokemon = (): Pokemon[] => {
  return Object.entries(pokemonData as any).map(([id, data]: [string, any]) => ({
    id,
    name: data.name,
    base_hp: data.base_hp,
    base_attack: data.base_attack,
    base_defense: data.base_defense,
    base_sp_attack: data.base_sp_attack,
    base_sp_defense: data.base_sp_defense,
    base_speed: data.base_speed,
    types: data.types || [],
    height: data.height,
    weight: typeof data.weight === 'number' ? data.weight : parseFloat(data.weight) || 0
  }));
};

export const getAllMoves = (): Move[] => {
  return Object.entries(movesData as any).map(([name, data]: [string, any]) => ({
    name,
    type: data.type,
    category: data.category as 'Physical' | 'Special' | 'Status',
    power: data.power || 0,
    accuracy: data.accuracy || 100,
    pp: data.pp || 5
  }));
};

export const getAllNatures = (): Nature[] => {
  return Object.entries(naturesData as any).map(([name, data]: [string, any]) => ({
    name,
    increase: data.increase,
    decrease: data.decrease
  }));
};

export const getAllAbilities = (): string[] => {
  // Extract ability names from the abilities array
  const abilities = (abilitiesData as any[]).map((item: any) => item.ability);
  return abilities.sort();
};

export const getAllItems = (): string[] => {
  // Extract item names from the items array
  const items = (itemsData as any[]).map((item: any) => item.item);
  return items.sort();
};

// Load custom sets from localStorage
export const getCustomSets = (): { [key: string]: PokemonSet } => {
  const customSets: { [key: string]: PokemonSet } = {};

  console.log('Loading custom sets from localStorage...');
  console.log('localStorage length:', localStorage.length);

  try {
    // Iterate through localStorage to find saved teams
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      console.log('Checking key:', key);
      if (key?.startsWith('pokemon-team-')) {
        const teamData = localStorage.getItem(key);
        console.log('Found team:', key, 'data length:', teamData?.length);
        if (teamData) {
          try {
            const parsed = JSON.parse(teamData);
            const teamName = parsed.name || key.replace('pokemon-team-', '');

            // Parse the Showdown format data to extract Pokemon
            const showdownText = parsed.data || '';
            const pokemonBlocks = showdownText.split(/\n\s*\n/).filter((block: string) => block.trim());

            pokemonBlocks.forEach((block: string, index: number) => {
              const lines = block.split('\n').map((line: string) => line.trim()).filter((line: string) => line);
              if (lines.length === 0) return;

              // Parse first line to get species name
              let firstLine = lines[0];
              let species = '';

              // Remove item
              firstLine = firstLine.replace(/@\s*.+$/, '').trim();
              // Remove gender
              firstLine = firstLine.replace(/\([MF]\)/, '').trim();
              // Extract species from (Species) or use whole line
              const speciesMatch = firstLine.match(/^(.+?)\s*\((.+?)\)$/);
              if (speciesMatch) {
                species = speciesMatch[2].trim();
              } else {
                species = firstLine.trim();
              }

              // Find Pokemon by name to get pokedexid
              const pokemon = getAllPokemon().find(p => p.name.toLowerCase() === species.toLowerCase());
              if (!pokemon) return;

              // Parse other fields
              let nature = 'Hardy';
              let ability = '';
              let item = '';
              let level = 100;
              const moves: string[] = [];
              const evs = { hp: 0, attack: 0, defense: 0, sp_attack: 0, sp_defense: 0, speed: 0 };
              const ivs = { hp: 31, attack: 31, defense: 31, sp_attack: 31, sp_defense: 31, speed: 31 };

              // Extract item from original first line
              const itemMatch = lines[0].match(/@\s*(.+)$/);
              if (itemMatch) item = itemMatch[1].trim();

              lines.slice(1).forEach(line => {
                if (line.startsWith('-')) {
                  const move = line.substring(1).trim();
                  if (moves.length < 4) moves.push(move);
                } else if (line.startsWith('Ability:')) {
                  ability = line.substring(8).trim();
                } else if (line.startsWith('Level:')) {
                  level = parseInt(line.substring(6).trim()) || 100;
                } else if (line.includes('Nature')) {
                  nature = line.replace('Nature', '').trim();
                } else if (line.startsWith('EVs:')) {
                  const evString = line.substring(4).trim();
                  const evParts = evString.split('/').map(p => p.trim());
                  evParts.forEach(part => {
                    const match = part.match(/(\d+)\s+(\w+)/);
                    if (match) {
                      const value = parseInt(match[1]);
                      const stat = match[2];
                      if (stat === 'HP') evs.hp = value;
                      else if (stat === 'Atk') evs.attack = value;
                      else if (stat === 'Def') evs.defense = value;
                      else if (stat === 'SpA') evs.sp_attack = value;
                      else if (stat === 'SpD') evs.sp_defense = value;
                      else if (stat === 'Spe') evs.speed = value;
                    }
                  });
                } else if (line.startsWith('IVs:')) {
                  const ivString = line.substring(4).trim();
                  const ivParts = ivString.split('/').map(p => p.trim());
                  ivParts.forEach(part => {
                    const match = part.match(/(\d+)\s+(\w+)/);
                    if (match) {
                      const value = parseInt(match[1]);
                      const stat = match[2];
                      if (stat === 'HP') ivs.hp = value;
                      else if (stat === 'Atk') ivs.attack = value;
                      else if (stat === 'Def') ivs.defense = value;
                      else if (stat === 'SpA') ivs.sp_attack = value;
                      else if (stat === 'SpD') ivs.sp_defense = value;
                      else if (stat === 'Spe') ivs.speed = value;
                    }
                  });
                }
              });

              // Create set name
              const setName = `${teamName} - ${species}${pokemonBlocks.length > 1 ? ` #${index + 1}` : ''}`;

              customSets[setName] = {
                name: setName,
                category: 'Custom',
                pokedexid: pokemon.id,
                nature,
                ability,
                level,
                item: item || '(None)',
                moves,
                hp_iv: ivs.hp,
                atk_iv: ivs.attack,
                def_iv: ivs.defense,
                sp_atk_iv: ivs.sp_attack,
                sp_def_iv: ivs.sp_defense,
                spd_iv: ivs.speed,
                hp_ev: evs.hp,
                atk_ev: evs.attack,
                def_ev: evs.defense,
                sp_atk_ev: evs.sp_attack,
                sp_def_ev: evs.sp_defense,
                spd_ev: evs.speed
              };
            });
          } catch (e) {
            console.error('Error parsing custom set:', e);
          }
        }
      }
    }
  } catch (e) {
    console.error('Error loading custom sets:', e);
  }

  return customSets;
};

// Sets data functions
export const getAllSets = (): { [key: string]: PokemonSet } => {
  const allSets: { [key: string]: PokemonSet } = {};

  // Process all dynamically loaded set files
  Object.entries(setFilesData).forEach(([fileName, data]) => {
    // Convert filename to category name (e.g., 'random_sets' -> 'Random')
    const prefix = fileName
      .split('_')[0]
      .charAt(0).toUpperCase() + fileName.split('_')[0].slice(1);

    Object.entries(data as any).forEach(([setName, setData]: [string, any]) => {
      allSets[setName] = {
        name: setName,
        category: prefix,
        pokedexid: setData.pokedexid,
        nature: setData.nature,
        ability: setData.ability,
        level: setData.level,
        item: setData.item,
        moves: setData.moves || [],
        hp_iv: setData.hp_iv || 31,
        atk_iv: setData.atk_iv || 31,
        def_iv: setData.def_iv || 31,
        sp_atk_iv: setData.sp_atk_iv || 31,
        sp_def_iv: setData.sp_def_iv || 31,
        spd_iv: setData.spd_iv || 31,
        hp_ev: setData.hp_ev || 0,
        atk_ev: setData.atk_ev || 0,
        def_ev: setData.def_ev || 0,
        sp_atk_ev: setData.sp_atk_ev || 0,
        sp_def_ev: setData.sp_def_ev || 0,
        spd_ev: setData.spd_ev || 0
      };
    });
  });

  // Add custom sets from localStorage
  const customSets = getCustomSets();
  Object.assign(allSets, customSets);

  return allSets;
};

export const getSetCategories = (): string[] => {
  const customSets = getCustomSets();
  const hasCustomSets = Object.keys(customSets).length > 0;

  const categories = ['All', 'Base', 'Random', 'Dungeon'];
  if (hasCustomSets) {
    categories.push('Custom');
  }
  return categories;
};

export const getPokemonSetsByCategory = (category: string): string[] => {
  const allSets = getAllSets();

  let filtered: PokemonSet[];
  if (!category || category === 'All') {
    filtered = Object.values(allSets);
  } else {
    filtered = Object.values(allSets).filter(set => set.category === category);
  }

  // Group sets by Pokemon name
  const groupedSets: { [pokemonName: string]: string[] } = {};

  filtered.forEach(set => {
    const pokemon = getPokemonById(set.pokedexid);
    if (pokemon) {
      const pokemonName = pokemon.name;
      if (!groupedSets[pokemonName]) {
        groupedSets[pokemonName] = [];
      }
      groupedSets[pokemonName].push(set.name);
    }
  });

  // Create flattened list with Pokemon names as headers and sets as options
  const result: string[] = [];
  Object.keys(groupedSets).sort().forEach(pokemonName => {
    result.push(`--- ${pokemonName} ---`); // Header separator
    groupedSets[pokemonName].forEach(setName => {
      result.push(setName);
    });
  });

  return result;
};

export const getSetByName = (setName: string): PokemonSet | null => {
  if (!setName) return null;
  const allSets = getAllSets();
  return allSets[setName] || null;
};

// Utility functions
export const getPokemonById = (id: string): Pokemon | undefined => {
  const pokemon = getAllPokemon().find(p => p.id === id);
  return pokemon;
};

export const getPokemonByName = (name: string): Pokemon | undefined => {
  const pokemon = getAllPokemon().find(p => p.name === name);
  return pokemon;
};

export const getMoveByName = (name: string): Move | undefined => {
  const move = getAllMoves().find(m => m.name === name);
  return move;
};

export const calculateStat = (
  baseStat: number,
  iv: number,
  ev: number,
  level: number,
  nature: Nature | null,
  statName: string,
  isHP: boolean = false
): number => {
  // Pokemon stat calculation formula
  let stat: number;

  if (isHP) {
    stat = Math.floor(((2 * baseStat + iv + Math.floor(ev / 4)) * level) / 100) + level + 10;
  } else {
    stat = Math.floor(((2 * baseStat + iv + Math.floor(ev / 4)) * level) / 100) + 5;

    // Apply nature modifier
    if (nature) {
      if (nature.increase === statName) {
        stat = Math.floor(stat * 1.1);
      } else if (nature.decrease === statName) {
        stat = Math.floor(stat * 0.9);
      }
    }
  }

  return stat;
};

export const applyStatStage = (stat: number, stage: number): number => {
  // Pokemon stat stage multipliers
  const multipliers = {
    '-6': 2/8, '-5': 2/7, '-4': 2/6, '-3': 2/5, '-2': 2/4, '-1': 2/3,
    '0': 1,
    '1': 3/2, '2': 4/2, '3': 5/2, '4': 6/2, '5': 7/2, '6': 8/2
  };

  const multiplier = multipliers[stage.toString() as keyof typeof multipliers] || 1;
  return Math.floor(stat * multiplier);
};

// Pokemon lookup helpers
export const searchPokemon = (query: string): Pokemon[] => {
  const allPokemon = getAllPokemon();
  if (!query.trim()) return allPokemon;

  const lowercaseQuery = query.toLowerCase();
  return allPokemon.filter(pokemon =>
    pokemon.name.toLowerCase().includes(lowercaseQuery) ||
    pokemon.id.includes(query)
  );
};

export const searchMoves = (query: string): Move[] => {
  const allMoves = getAllMoves();
  if (!query.trim()) return allMoves;

  const lowercaseQuery = query.toLowerCase();
  return allMoves.filter(move =>
    move.name.toLowerCase().includes(lowercaseQuery) ||
    move.type.toLowerCase().includes(lowercaseQuery)
  );
};

// Default/empty values
export const getDefaultPokemon = (): Pokemon => ({
  id: '460',
  name: 'Abomasnow',
  base_hp: 90,
  base_attack: 92,
  base_defense: 75,
  base_sp_attack: 92,
  base_sp_defense: 85,
  base_speed: 60,
  types: ['Grass', 'Ice']
});

export const getDefaultMove = (): Move => ({
  name: '(No Move)',
  type: 'Normal',
  category: 'Physical',
  power: 0,
  accuracy: 100,
  pp: 5
});
